import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, ActivityTypeBadge, StatusPill, useFetch, Modal, Field, TextInput, Select, Textarea, PrimaryButton } from "../../components/crm/ui";
import ActivityAttachments from "../../components/crm/ActivityAttachments";
import TicketDetailModal from "../../components/crm/TicketDetailModal";
import CompleteInstallationModal from "../../components/crm/CompleteInstallationModal";
import OpportunityAiInsights from "../../components/crm/OpportunityAiInsights";
import WhatsAppTracker from "../../components/crm/WhatsAppTracker";
import { ChevronLeft, CheckCircle2, Loader2, Circle, Plus, Trash2, ShieldCheck, Calendar, Pencil, Check, X, Lock, FileCheck2, Wrench, HardHat, UserCog, MessageCircle } from "lucide-react";

const PHASE_NAMES = {
  1: "Due Diligence",
  2: "Solutioning & Alignment",
  3: "Regulatory & Approval Management",
  4: "Installation & Commissioning",
  5: "Subsidy Closure & After Sales Support",
};

const OPP_STATUSES = ["open", "won_verbal", "won", "on_hold", "dropped", "lost"];
const OPP_STATUS_TONE = {
  open: "info",
  active: "info",
  won_verbal: "warning",
  won: "success",
  on_hold: "warning",
  dropped: "muted",
  lost: "danger",
};
const GATED_PHASES = new Set([3, 4, 5]);
const UNLOCKED_STATUSES = new Set(["won_verbal", "won"]);
const QUOTE_STATUS_LABEL = {
  not_started: "Not Started",
  completed: "Completed",
  draft: "Draft",
  sent: "Sent",
  customer_evaluation_wip: "Customer Eval · WIP",
  verbally_confirmed_accepted: "Verbally Accepted",
  reprocessing_required: "Re-Processing",
  accepted: "Accepted",
  rejected: "Rejected",
};

const QUOTE_STATUS_TONE_V2 = {
  not_started:                 { bg: "bg-slate-100",   fg: "text-slate-600",   dot: "bg-slate-400" },
  completed:                   { bg: "bg-emerald-100", fg: "text-emerald-800", dot: "bg-emerald-500" },
  draft:                       { bg: "bg-slate-100",   fg: "text-slate-700",   dot: "bg-slate-500" },
  sent:                        { bg: "bg-sky-100",     fg: "text-sky-800",     dot: "bg-sky-500" },
  customer_evaluation_wip:     { bg: "bg-amber-100",   fg: "text-amber-800",   dot: "bg-amber-500" },
  verbally_confirmed_accepted: { bg: "bg-emerald-100", fg: "text-emerald-800", dot: "bg-emerald-500" },
  reprocessing_required:       { bg: "bg-orange-100",  fg: "text-orange-800",  dot: "bg-orange-500" },
  accepted:                    { bg: "bg-emerald-200", fg: "text-emerald-900", dot: "bg-emerald-600" },
  rejected:                    { bg: "bg-rose-100",    fg: "text-rose-800",    dot: "bg-rose-500" },
};

const QUOTE_STATUS_TONE = {
  not_started: "muted",
  draft: "warning",
  sent: "info",
  approved: "success",
  accepted: "success",
  rejected: "danger",
  completed: "success",
};

const STATUS_ICON = { pending: Circle, in_process: Loader2, completed: CheckCircle2, skipped: X };

// Installation-status → visual tone (from backend derivation)
const INSTALL_TONE = {
  not_logged:             { bg: "bg-slate-100",    fg: "text-slate-600",   dot: "bg-slate-400" },
  scheduled:              { bg: "bg-sky-50",       fg: "text-sky-800",     dot: "bg-sky-500" },
  in_progress:            { bg: "bg-amber-50",     fg: "text-amber-800",   dot: "bg-amber-500" },
  on_hold:                { bg: "bg-orange-50",    fg: "text-orange-800",  dot: "bg-orange-500" },
  support_required:       { bg: "bg-rose-50",      fg: "text-rose-800",    dot: "bg-rose-500" },
  installation_completed: { bg: "bg-emerald-50",   fg: "text-emerald-800", dot: "bg-emerald-500" },
  closed:                 { bg: "bg-emerald-100",  fg: "text-emerald-900", dot: "bg-emerald-600" },
};

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [active, setActive] = useState(1);
  const { data: employees } = useFetch("/sales-employees");
  const { data: customers } = useFetch("/customers");
  const { data: materials } = useFetch("/materials");
  const { data: serviceTeam } = useFetch("/service-team/search");

  // Installation completion modal
  const [installModal, setInstallModal] = useState(null); // { activity }

  // WhatsApp Activity tracker modal
  const [waOpen, setWaOpen] = useState(false);

  // Phase-4 "open new-installation ticket" modal
  const [ticketModal, setTicketModal] = useState(null);  // { activity }
  const [ticketSI, setTicketSI] = useState(null);          // selected service-team member
  const [ticketSIQuery, setTicketSIQuery] = useState("");
  const [ticketSIResults, setTicketSIResults] = useState([]);
  const [ticketMaterial, setTicketMaterial] = useState("");
  const [ticketSerial, setTicketSerial] = useState("");
  const [ticketSaving, setTicketSaving] = useState(false);

  useEffect(() => {
    if (!ticketModal) return;
    const t = setTimeout(() => {
      api.get(`/service-team/search?q=${encodeURIComponent(ticketSIQuery)}`)
        .then((r) => setTicketSIResults(r.data || []))
        .catch(() => setTicketSIResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [ticketSIQuery, ticketModal]);

  // Header inline edits state
  const [valueEditing, setValueEditing] = useState(false);
  const [valueDraft, setValueDraft] = useState(0);
  const [statusSaving, setStatusSaving] = useState(false);
  const [spocSaving, setSpocSaving] = useState(false);
  const [quoteStatus, setQuoteStatus] = useState(null);

  // Activity edit modal state
  const [editAct, setEditAct] = useState(null);
  const [editForm, setEditForm] = useState({ status: "", assigned_employee_id: "", notes: "", appointment_at: "" });
  const [editSaving, setEditSaving] = useState(false);

  // Phase-4 ticket-trigger: linked-ticket detail modal (admin/sales viewing existing ticket)
  const [linkedTicketId, setLinkedTicketId] = useState(null);
  const [linkedTicketLoading, setLinkedTicketLoading] = useState(false);

  async function openLinkedTicket(actId) {
    setLinkedTicketLoading(true);
    try {
      const r = await api.get("/service-tickets");
      const tk = (r.data || []).find((t) => t.linked_opp_activity_id === actId && t.linked_opp_id === opp.id);
      if (!tk) {
        toast.error("Linked ticket not found");
        return;
      }
      setLinkedTicketId(tk.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLinkedTicketLoading(false);
    }
  }

  function openActivityEdit(a) {
    // Phase-4 ticket-trigger: click-to-open the ticket flow directly
    if (isTicketTrigger(a)) {
      if (a.status === "completed") {
        // Ticket already opened — open its detail/edit modal in-place
        openLinkedTicket(a.id);
        return;
      }
      setTicketModal({ activity: a });
      setTicketSI(null); setTicketSIQuery(""); setTicketMaterial(""); setTicketSerial("");
      return;
    }
    setEditAct(a);
    setEditForm({
      status: a.status || "pending",
      assigned_employee_id: a.assigned_employee_id || "",
      notes: a.notes || "",
      // datetime-local input expects YYYY-MM-DDTHH:mm
      appointment_at: a.appointment_at ? a.appointment_at.slice(0, 16) : "",
    });
  }

  async function saveActivityEdit(e) {
    e.preventDefault();
    if (!editAct) return;
    // If user is closing an Installation Completion via the modal → defer to install flow
    if (editForm.status === "completed" && isInstallCompletionActivity(editAct) && editAct.status !== "completed") {
      setEditAct(null);
      setInstallModal({ activity: editAct });
      return;
    }
    setEditSaving(true);
    try {
      const params = new URLSearchParams();
      params.set("status", editForm.status);
      params.set("assigned_employee_id", editForm.assigned_employee_id || "");
      params.set("notes", editForm.notes || "");
      // appointment_at: send empty string to clear, or full ISO; "" handled server-side
      params.set("appointment_at", editForm.appointment_at || "");
      await api.put(`/opportunities/${opp.id}/activities/${editAct.id}?${params.toString()}`);
      toast.success("Activity updated");
      setEditAct(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setEditSaving(false);
    }
  }

  function load() {
    api.get(`/opportunities/${id}`).then((r) => { setOpp(r.data); setActive(r.data.current_phase || 1); });
    api.get(`/opportunities/${id}/quote-status`).then((r) => setQuoteStatus(r.data)).catch(() => setQuoteStatus(null));
  }

  async function saveValue() {
    const v = Number(valueDraft);
    if (Number.isNaN(v) || v < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      await api.put(`/opportunities/${opp.id}/value?estimated_value=${v}`);
      toast.success("Value updated");
      setValueEditing(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function saveStatus(s, reason = "") {
    setStatusSaving(true);
    try {
      const qs = new URLSearchParams({ status: s });
      if (reason) qs.append("reason", reason);
      await api.put(`/opportunities/${opp.id}/status?${qs.toString()}`);
      toast.success(`Status: ${s.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setStatusSaving(false);
    }
  }

  const NEGATIVE_OPP_STATUSES = ["dropped", "lost", "on_hold"];

  // When the user picks a negative status, open the reason modal instead of saving immediately.
  const [reasonModal, setReasonModal] = useState(null); // { newStatus, reason }
  function onStatusSelect(s) {
    if (NEGATIVE_OPP_STATUSES.includes(s)) {
      setReasonModal({ newStatus: s, reason: opp?.status_reason || "" });
      return;
    }
    saveStatus(s, "");
  }

  async function saveSpoc(empId) {
    setSpocSaving(true);
    try {
      await api.put(`/opportunities/${opp.id}/spoc?sales_employee_id=${empId || ""}`);
      toast.success(empId ? "SPOC updated" : "SPOC cleared");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSpocSaving(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!opp) return <div className="text-sm font-mono text-[#4A5568]">Loading…</div>;

  const cust = customers.find((c) => c.id === opp.customer_id);
  const phaseActs = opp.activities.filter((a) => a.phase === active);

  function isInstallCompletionActivity(act) {
    return act && act.phase === 4 && act.type === "closure" && /completion confirmation/i.test(act.name);
  }

  function isTicketTrigger(act) {
    return act && act.phase === 4 && act.type === "ticket";
  }

  async function updateActivity(act, patch) {
    // Intercept: Phase-4 "ticket" trigger activity → open the create-ticket modal
    if (isTicketTrigger(act) && act.status !== "completed" && patch.status === "completed") {
      setTicketModal({ activity: act });
      setTicketSI(null); setTicketSIQuery(""); setTicketMaterial(""); setTicketSerial("");
      return;
    }
    // Intercept: if user is trying to mark "Installation Completion confirmation" as completed → open modal
    if (patch.status === "completed" && isInstallCompletionActivity(act) && act.status !== "completed") {
      setInstallModal({ activity: act });
      return;
    }
    const params = new URLSearchParams();
    if (patch.status) params.set("status", patch.status);
    if (patch.assigned_employee_id !== undefined) params.set("assigned_employee_id", patch.assigned_employee_id);
    if (patch.notes !== undefined) params.set("notes", patch.notes);
    try {
      await api.put(`/opportunities/${opp.id}/activities/${act.id}?${params.toString()}`);
      toast.success("Activity updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  return (
    <div data-testid="opportunity-detail">
      <Link to="/crm/opportunities" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#4A5568] hover:text-[#1F4E2C]">
        <ChevronLeft className="w-3 h-3" /> Back to Opportunities
      </Link>
      {opp.lead_code && (
        <Link
          to={`/crm/leads`}
          data-testid="opp-lead-backlink"
          className="ml-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#94A3B8] hover:text-[#1F4E2C]"
          title="Originating lead"
        >
          from lead <span className="font-bold text-[#1F4E2C]">{opp.lead_code}</span>
        </Link>
      )}
      <PageHeader
        overline={`Opportunity / ${opp.code || opp.id.slice(0, 8)}`}
        title={opp.name}
        action={(() => {
          const key = opp.installation_status || "not_logged";
          const tone = INSTALL_TONE[key] || INSTALL_TONE.not_logged;
          const si = opp.service_incharge;
          const qs = quoteStatus;
          const qTone = qs?.submitted
            ? (QUOTE_STATUS_TONE_V2[qs.status] || QUOTE_STATUS_TONE_V2.draft)
            : null;
          return (
            <div className="flex items-stretch gap-4">
              {qs?.submitted && qTone && (
                <div
                  data-testid="opp-header-quote-chip"
                  className={"flex flex-col gap-1.5 items-start border-l-4 border-current pl-4 " + qTone.fg}
                >
                  <div className="flex items-center gap-2">
                    <FileCheck2 className="w-4 h-4" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">Quotation</span>
                    <span className={"inline-block w-2 h-2 rounded-full " + qTone.dot} />
                    <span
                      data-testid="opp-header-quote-status"
                      className="font-display font-bold text-sm"
                    >
                      {QUOTE_STATUS_LABEL[qs.status] || qs.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">Quote #</span>
                    <Link
                      to={`/crm/quotations/${qs.quotation_id}`}
                      data-testid="opp-header-quote-number"
                      className="font-display font-semibold text-sm underline decoration-dotted hover:decoration-solid"
                    >
                      {qs.quotation_number}
                    </Link>
                    <span className="font-mono text-[11px] opacity-80">· ₹{Number(qs.total || 0).toLocaleString("en-IN")}</span>
                  </div>
                </div>
              )}
              <div
                data-testid="opp-header-install-chip"
                className={"flex flex-col gap-1.5 items-start border-l-4 border-current pl-4 " + tone.fg}
              >
                <div className="flex items-center gap-2">
                  <HardHat className="w-4 h-4" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">
                    Installation
                  </span>
                  <span className={"inline-block w-2 h-2 rounded-full " + tone.dot} />
                  <span
                    data-testid="opp-header-install-status"
                    className="font-display font-bold text-sm"
                  >
                    {opp.installation_status_label || "Not Logged"}
                  </span>
                  {opp.ticket_number && (
                    <Link
                      to="/crm/service-tickets"
                      className="font-mono text-[10px] uppercase tracking-widest underline decoration-dotted opacity-80 hover:opacity-100"
                    >
                      {opp.ticket_number}
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <UserCog className="w-4 h-4 opacity-80" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">
                    Service Incharge
                  </span>
                  <span
                    data-testid="opp-header-service-incharge"
                    className="font-display font-semibold text-sm"
                  >
                    {si?.name || <span className="italic opacity-70 font-normal">Not Assigned</span>}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setWaOpen(true)}
                data-testid="opp-whatsapp-activity-btn"
                className="self-start flex items-center gap-1.5 border border-[#DDE5D8] px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C]"
                title="View WhatsApp delivery activity for this opportunity"
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Activity
              </button>
            </div>
          );
        })()}
      />

      {/* Installation Status + Service Incharge banner (redundant on wide screens, useful on narrow) */}
      {(() => {
        const key = opp.installation_status || "not_logged";
        const tone = INSTALL_TONE[key] || INSTALL_TONE.not_logged;
        const si = opp.service_incharge;
        return (
          <div
            data-testid="opp-install-banner"
            className={"mb-6 flex flex-wrap items-center gap-3 px-4 py-2.5 border lg:hidden " + tone.bg + " " + tone.fg + " border-current/20"}
          >
            <div className="flex items-center gap-2">
              <HardHat className="w-4 h-4" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">Installation</span>
              <span className={"inline-block w-2 h-2 rounded-full " + tone.dot} />
              <span
                data-testid="opp-install-status-label"
                className="font-display font-semibold text-sm"
              >
                {opp.installation_status_label || "Not Logged"}
              </span>
              {opp.ticket_number && (
                <Link
                  to="/crm/service-tickets"
                  data-testid="opp-install-ticket-ref"
                  className="ml-1 font-mono text-[10px] uppercase tracking-widest underline decoration-dotted opacity-80 hover:opacity-100"
                >
                  {opp.ticket_number}
                </Link>
              )}
            </div>
            <div className="h-4 w-px bg-current opacity-20" />
            <div className="flex items-center gap-2">
              <UserCog className="w-4 h-4" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">Service Incharge</span>
              <span
                data-testid="opp-service-incharge"
                className="font-display font-semibold text-sm"
              >
                {si?.name || <span className="italic opacity-70">Not Assigned</span>}
              </span>
            </div>
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-5 gap-4 mb-6">
        {/* Customer */}
        <div className="border border-[#DDE5D8] bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Customer</div>
          <div className="font-display text-lg mt-1 font-semibold">{cust?.customer_name}</div>
          <div className="text-xs text-[#475569] font-mono mt-1 truncate">{cust?.phone} · {cust?.email}</div>
        </div>

        {/* Opportunity Value — inline editable */}
        <div className="border border-[#DDE5D8] bg-white p-5">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Opportunity Value</div>
            {!valueEditing && (
              <button
                onClick={() => { setValueDraft(opp.estimated_value || 0); setValueEditing(true); }}
                data-testid="edit-value-btn"
                className="p-1 text-[#475569] hover:text-[#1F4E2C]"
                title="Edit value"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
          {valueEditing ? (
            <div className="mt-2 flex items-center gap-1">
              <span className="font-display text-2xl font-bold text-[#1F4E2C]">₹</span>
              <input
                type="number"
                autoFocus
                value={valueDraft}
                onChange={(e) => setValueDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveValue(); if (e.key === "Escape") setValueEditing(false); }}
                data-testid="value-input"
                className="font-display text-2xl font-bold text-[#1F4E2C] bg-[#F4F6F1] border border-[#1F4E2C] px-2 py-0.5 w-full outline-none"
              />
              <button onClick={saveValue} data-testid="value-save" className="p-1 bg-[#1F4E2C] text-white"><Check className="w-3 h-3" /></button>
              <button onClick={() => setValueEditing(false)} data-testid="value-cancel" className="p-1 text-[#475569]"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <div className="font-display text-2xl font-bold text-[#1F4E2C] mt-1" data-testid="opp-value-display">
              ₹{Number(opp.estimated_value || 0).toLocaleString("en-IN")}
            </div>
          )}
          <div className="text-xs text-[#475569] font-mono mt-1">Close by: {opp.expected_close_date || "—"}</div>
        </div>

        {/* Status — inline dropdown */}
        <div className="border border-[#DDE5D8] bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Status</div>
          <div className="mt-2">
            <Select
              value={OPP_STATUSES.includes(opp.status) ? opp.status : "open"}
              onChange={(e) => onStatusSelect(e.target.value)}
              disabled={statusSaving}
              data-testid="opp-status-select"
            >
              <option value="open">Open</option>
              <option value="won_verbal">Won · Verbal</option>
              <option value="won">Won</option>
              <option value="on_hold">On Hold</option>
              <option value="dropped">Dropped</option>
              <option value="lost">Lost</option>
            </Select>
          </div>
          <div className="mt-2"><StatusPill value={(opp.status || "open").replace("_", " ")} tone={OPP_STATUS_TONE[opp.status] || "info"} /></div>
          {NEGATIVE_OPP_STATUSES.includes(opp.status) && opp.status_reason && (
            <div
              data-testid="opp-status-reason"
              className="mt-2 bg-red-50 border border-red-200 px-2.5 py-1.5 text-[11px] text-red-800"
            >
              <span className="font-mono uppercase tracking-widest text-[9px] text-red-600">Reason</span>
              <div className="mt-0.5 whitespace-pre-wrap">{opp.status_reason}</div>
            </div>
          )}
          <div className="text-xs text-[#475569] font-mono mt-2">Current phase: <span className="font-semibold text-[#0F1A12]">{opp.current_phase}</span> / 5</div>
        </div>

        {/* SPOC + Quote status */}
        <div className="border border-[#DDE5D8] bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">SPOC · Sales Employee</div>
          <div className="mt-2">
            <Select
              value={opp.sales_employee_id || ""}
              onChange={(e) => saveSpoc(e.target.value)}
              disabled={spocSaving}
              data-testid="opp-spoc-select"
            >
              <option value="">— Unassigned —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.territory}</option>)}
            </Select>
          </div>

          <div className="mt-3 pt-3 border-t border-[#EEF2EA]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] flex items-center gap-1">
              <FileCheck2 className="w-3 h-3" /> Quote Submission
            </div>
            <div className="mt-1.5" data-testid="quote-status-pill">
              {quoteStatus?.submitted ? (() => {
                const t = QUOTE_STATUS_TONE_V2[quoteStatus.status] || QUOTE_STATUS_TONE_V2.draft;
                return (
                  <div className="space-y-1.5">
                    <span
                      data-testid="opp-quote-status-chip"
                      className={"inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10.5px] uppercase tracking-wider font-semibold " + t.bg + " " + t.fg}
                    >
                      <span className={"w-1.5 h-1.5 rounded-full " + t.dot} />
                      {QUOTE_STATUS_LABEL[quoteStatus.status] || quoteStatus.status}
                    </span>
                    <div className="font-mono text-[10px] text-[#475569]">
                      <Link to={`/crm/quotations/${quoteStatus.quotation_id}`} data-testid="opp-quote-open" className="hover:underline text-[#1F4E2C] font-semibold">
                        {quoteStatus.quotation_number}
                      </Link>
                      <span className="mx-1">·</span>₹{Number(quoteStatus.total || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                );
              })() : (
                <Link to={`/crm/quotations/new?opp=${opp.id}`} data-testid="opp-quote-create" className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline">
                  Not submitted · Create →
                </Link>
              )}
            </div>
          </div>

          {/* SC referral & payment panel */}
          {opp.sc_user_id && (
            <ScPaymentPanel oppId={opp.id} opp={opp} onChange={load} />
          )}
        </div>

        {/* Installation Status — derived from Phase-4 "New Installation Service Ticket Logged" */}
        {(() => {
          const key = opp.installation_status || "not_logged";
          const tone = INSTALL_TONE[key] || INSTALL_TONE.not_logged;
          const si = opp.service_incharge;
          return (
            <div
              className={"border p-5 " + tone.bg + " " + tone.fg + " border-current/20"}
              data-testid="opp-install-card"
            >
              <div className="flex items-center gap-1.5">
                <HardHat className="w-3.5 h-3.5" />
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">
                  Installation
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className={"inline-block w-2.5 h-2.5 rounded-full " + tone.dot} />
                <div className="font-display text-base font-semibold leading-tight" data-testid="opp-install-card-status">
                  {opp.installation_status_label || "Not Logged"}
                </div>
              </div>
              {opp.ticket_number && (
                <div className="mt-2 font-mono text-[10px] opacity-80" data-testid="opp-install-card-ticket">
                  Ticket · <span className="font-semibold">{opp.ticket_number}</span>
                </div>
              )}
              {opp.installation_photos && opp.installation_photos.length > 0 && (
                <div className="mt-3" data-testid="opp-install-photo-thumbs">
                  <div className="font-mono text-[10px] uppercase tracking-widest opacity-80 mb-1.5">
                    Site Photos · {opp.installation_photo_count}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {opp.installation_photos.slice(0, 6).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => opp.ticket_id && setLinkedTicketId(opp.ticket_id)}
                        data-testid={`opp-install-photo-${p.id}`}
                        className="aspect-square bg-white/50 overflow-hidden border border-current/20 rounded-sm group"
                        title={p.filename}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.id}`}
                          alt={p.filename}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </button>
                    ))}
                  </div>
                  {opp.installation_photo_count > 6 && (
                    <div className="mt-1 font-mono text-[9px] opacity-70">
                      +{opp.installation_photo_count - 6} more · open ticket to view
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-current/15">
                <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest opacity-80">
                  <UserCog className="w-3 h-3" /> Service Incharge
                </div>
                <div className="mt-1 font-display text-sm font-semibold" data-testid="opp-install-card-si">
                  {si?.name || <span className="italic opacity-70 font-normal">Not Assigned</span>}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* AI Insights (Gemini 3.1 Pro) — on-demand executive brief for this deal */}
      <OpportunityAiInsights oppId={opp.id} />

      {/* Phase tabs — Phases 3/4/5 locked unless status is Won Verbal / Won */}
      <div className="flex gap-1 mb-6 overflow-x-auto no-scrollbar border-b border-[#DDE5D8]">
        {[1, 2, 3, 4, 5].map((p) => {
          const acts = opp.activities.filter((a) => a.phase === p);
          const done = acts.filter((a) => a.status === "completed").length;
          const locked = GATED_PHASES.has(p) && !UNLOCKED_STATUSES.has(opp.status);
          return (
            <button
              key={p}
              onClick={() => {
                if (locked) {
                  toast.error(`Phase ${p} is locked. Set opportunity to Won Verbal / Won to unlock.`);
                  return;
                }
                setActive(p);
              }}
              data-testid={`phase-tab-${p}`}
              className={"px-4 py-3 font-mono text-xs uppercase tracking-widest border-b-2 transition-colors text-left relative " +
                (locked
                  ? "border-transparent text-[#94A3B8] cursor-not-allowed bg-[#F4F6F1]/50"
                  : active === p
                    ? "border-[#1F4E2C] text-[#0F1A12]"
                    : "border-transparent text-[#475569] hover:text-[#0F1A12]")}>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] opacity-70">Phase {p}</span>
                {locked && <Lock className="w-3 h-3" />}
              </div>
              <div>{PHASE_NAMES[p]}</div>
              <div className="text-[10px] mt-0.5">{done}/{acts.length}</div>
            </button>
          );
        })}
      </div>

      {/* Locked phase notice */}
      {GATED_PHASES.has(active) && !UNLOCKED_STATUSES.has(opp.status) && (
        <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6 flex items-center gap-3" data-testid="phase-locked-notice">
          <Lock className="w-5 h-5 text-amber-700" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">Phase {active} is locked.</span> Mark this opportunity as <span className="font-mono uppercase">Won Verbal</span> or <span className="font-mono uppercase">Won</span> to start working on these activities.
          </div>
        </div>
      )}

      {/* Activities */}
      <div className="space-y-3">
        {phaseActs.map((a) => {
          const Icon = STATUS_ICON[a.status] || Circle;
          const emp = employees.find((e) => e.id === a.assigned_employee_id);
          const isCompletion = isInstallCompletionActivity(a);
          const isTicket = isTicketTrigger(a);
          // For ticket-trigger activities, surface the linked SI by parsing the system note
          let linkedSiLabel = null;
          if (isTicket && a.status === "completed" && a.notes) {
            const m = a.notes.match(/SI:\s*(.+)$/);
            if (m) linkedSiLabel = m[1].trim();
          }
          return (
            <div
              key={a.id}
              onClick={() => openActivityEdit(a)}
              role="button"
              data-testid={`activity-${a.id}`}
              className={"border bg-white p-4 cursor-pointer hover:border-[#1F4E2C] transition-colors " +
                (isTicket ? "border-rose-200 bg-rose-50/30 " : "") +
                (isCompletion ? "border-[#1F4E2C]/40 bg-[#1F4E2C]/5" : "border-[#E2E4DC]")}
            >
              <div className="flex items-start gap-4">
                <Icon className={"w-5 h-5 mt-0.5 flex-shrink-0 " +
                  (a.status === "completed" ? "text-emerald-600" : a.status === "in_process" ? "text-[#1F4E2C] animate-spin" : "text-[#94A3B8]")} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-medium text-[#0A1128]">{a.name}</div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <ActivityTypeBadge type={a.type} />
                        {isCompletion && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C]">
                            <ShieldCheck className="w-3 h-3" /> Auto-creates installed base
                          </span>
                        )}
                        {isTicket && a.status !== "completed" && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-rose-700">
                            <Wrench className="w-3 h-3" /> Assign Service Incharge to open ticket
                          </span>
                        )}
                        {a.appointment_at && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5">
                            <Calendar className="w-3 h-3" />
                            {new Date(a.appointment_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {isTicket ? (
                        a.status === "completed" ? (
                          <span
                            data-testid={`activity-si-locked-${a.id}`}
                            className="text-[11px] font-mono px-2 py-1.5 bg-white border border-rose-200 text-rose-700"
                            title="Service Incharge assigned. Click row to view linked ticket."
                          >
                            SI: {linkedSiLabel || "Assigned"}
                          </span>
                        ) : (
                          <select
                            value=""
                            onChange={(e) => {
                              const sm = serviceTeam.find((s) => s.user_id === e.target.value);
                              if (!sm) return;
                              setTicketModal({ activity: a });
                              setTicketSI(sm);
                              setTicketSIQuery(sm.label);
                              setTicketMaterial("");
                              setTicketSerial("");
                            }}
                            data-testid={`activity-si-assign-${a.id}`}
                            className="text-xs border border-rose-300 bg-white px-2 py-1.5 font-mono"
                          >
                            <option value="">— Assign Service Incharge —</option>
                            {serviceTeam.map((sm) => (
                              <option key={sm.user_id} value={sm.user_id}>
                                {sm.label} · {sm.type === "service_franchisee" ? "Franchisee" : "Manager"}
                              </option>
                            ))}
                          </select>
                        )
                      ) : (
                        <>
                          <select value={a.assigned_employee_id || ""}
                            onChange={(e) => updateActivity(a, { assigned_employee_id: e.target.value })}
                            data-testid={`activity-assign-${a.id}`}
                            className="text-xs border border-[#E2E4DC] px-2 py-1.5 font-mono">
                            <option value="">Unassigned</option>
                            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                          </select>
                          <select value={a.status}
                            onChange={(e) => updateActivity(a, { status: e.target.value })}
                            data-testid={`activity-status-${a.id}`}
                            className="text-xs border border-[#E2E4DC] px-2 py-1.5 font-mono uppercase tracking-widest">
                            <option value="pending">Pending</option>
                            <option value="in_process">In Process</option>
                            <option value="completed">Completed</option>
                          </select>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => openActivityEdit(a)}
                        data-testid={`activity-edit-${a.id}`}
                        title={isTicket && a.status === "completed" ? "View linked ticket" : "Edit activity"}
                        className="p-1.5 border border-[#E2E4DC] hover:border-[#1F4E2C] hover:text-[#1F4E2C] text-[#475569]"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {!isTicket && emp && <div className="text-[10px] mt-2 font-mono uppercase tracking-widest text-[#4A5568]">→ {emp.name}</div>}
                  {a.notes && (
                    <div className="text-xs text-[#475569] mt-2 line-clamp-2 whitespace-pre-wrap" onClick={(e) => e.stopPropagation()}>{a.notes}</div>
                  )}
                  <div onClick={(e) => e.stopPropagation()}>
                    <ActivityAttachments opportunityId={opp.id} activityId={a.id} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ACTIVITY EDIT MODAL */}
      <Modal open={!!editAct} onClose={() => setEditAct(null)} title={editAct?.name || "Edit Activity"} wide>
        {editAct && (
          <form onSubmit={saveActivityEdit} className="space-y-4" data-testid="activity-edit-form">
            <div className="flex items-center gap-2 flex-wrap">
              <ActivityTypeBadge type={editAct.type} />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Phase {editAct.phase}</span>
              {isInstallCompletionActivity(editAct) && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C]">
                  <ShieldCheck className="w-3 h-3" /> Closure auto-creates Installed Base
                </span>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Status">
                <Select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  data-testid="edit-status"
                >
                  <option value="pending">Pending</option>
                  <option value="in_process">In Process</option>
                  <option value="completed">Completed</option>
                </Select>
              </Field>
              <Field label="Assigned Employee">
                <Select
                  value={editForm.assigned_employee_id}
                  onChange={(e) => setEditForm({ ...editForm, assigned_employee_id: e.target.value })}
                  data-testid="edit-assigned"
                >
                  <option value="">— Unassigned —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.territory}</option>)}
                </Select>
              </Field>
              {(editAct.type === "appointment" || editAct.appointment_at) && (
                <Field label="Appointment date & time" className="md:col-span-2">
                  <TextInput
                    type="datetime-local"
                    value={editForm.appointment_at}
                    onChange={(e) => setEditForm({ ...editForm, appointment_at: e.target.value })}
                    data-testid="edit-appointment-at"
                  />
                </Field>
              )}
              <Field label="Notes" className="md:col-span-2">
                <Textarea
                  rows={5}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  data-testid="edit-notes"
                />
              </Field>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditAct(null)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#475569]">Cancel</button>
              <PrimaryButton type="submit" disabled={editSaving} testid="edit-save-btn">
                {editSaving ? "Saving…" : "Save"}
              </PrimaryButton>
            </div>
          </form>
        )}
      </Modal>

      {/* INSTALLATION COMPLETION MODAL — auto-creates Installed Base */}
      {/* ── New-Installation Service Ticket modal (Phase-4 trigger) ── */}
      <Modal open={!!ticketModal} onClose={() => setTicketModal(null)} title="Create New-Installation Service Ticket" wide>
        <div className="space-y-4" data-testid="ticket-trigger-modal">
          <p className="text-sm text-[#4A5568]">
            Marking <span className="font-semibold text-[#0A1128]">"New Installation Service Ticket Logged"</span> opens a Service Ticket of type <strong>New Installation</strong>.
            The selected Service Incharge will manage the remaining Phase-4 activities and assign technicians from their roster.
          </p>

          <Field label="Service Incharge (search Service Team)">
            <div className="relative">
              <TextInput
                placeholder="Type firm name, contact person, or email…"
                value={ticketSI ? `${ticketSI.label} · ${ticketSI.email}` : ticketSIQuery}
                onChange={(e) => { setTicketSI(null); setTicketSIQuery(e.target.value); }}
                data-testid="ticket-si-search"
              />
              {!ticketSI && ticketSIResults.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#DDE5D8] shadow-lg max-h-60 overflow-y-auto" data-testid="ticket-si-results">
                  {ticketSIResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => { setTicketSI(r); setTicketSIQuery(""); }}
                      data-testid={`ticket-si-pick-${r.id}`}
                      className="block w-full text-left px-3 py-2 hover:bg-[#F4F6F1] border-b border-[#EEF2EA]"
                    >
                      <div className="font-semibold text-sm text-[#0F1A12]">{r.label}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
                        {r.type === "service_franchisee" ? "Franchisee" : "Manager"} · {r.email} · {r.phone}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Equipment (optional)">
              <Select value={ticketMaterial} onChange={(e) => setTicketMaterial(e.target.value)} data-testid="ticket-material">
                <option value="">— Select material —</option>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Select>
            </Field>
            <Field label="Serial / Lot Code (optional)">
              <TextInput value={ticketSerial} onChange={(e) => setTicketSerial(e.target.value)} data-testid="ticket-serial" />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setTicketModal(null)} className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C]">Cancel</button>
            <PrimaryButton
              type="button"
              disabled={!ticketSI || ticketSaving}
              data-testid="ticket-trigger-create"
              onClick={async () => {
                if (!ticketSI) return;
                setTicketSaving(true);
                try {
                  await api.post(`/opportunities/${opp.id}/activities/${ticketModal.activity.id}/open-ticket`, {
                    service_incharge_id: ticketSI.user_id,
                    material_id: ticketMaterial || null,
                    serial_code: ticketSerial || null,
                  });
                  toast.success(`Service Ticket opened. Assigned to ${ticketSI.label}.`);
                  setTicketModal(null);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || err.message);
                } finally { setTicketSaving(false); }
              }}
            >
              {ticketSaving ? "Creating…" : "Create Ticket"}
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <CompleteInstallationModal
        open={!!installModal}
        oid={opp.id}
        activityId={installModal?.activity?.id}
        customerName={cust?.customer_name}
        onClose={() => setInstallModal(null)}
        onSuccess={() => { setInstallModal(null); load(); }}
      />


      <TicketDetailModal
        open={!!linkedTicketId}
        ticketId={linkedTicketId}
        onClose={() => setLinkedTicketId(null)}
        onChanged={() => { load(); }}
      />

      {/* Reason modal — required when marking opp as dropped/lost/on_hold */}
      <Modal
        open={!!reasonModal}
        onClose={() => setReasonModal(null)}
        title={reasonModal ? `Mark as ${reasonModal.newStatus.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())} — share the reason` : ""}
      >
        {reasonModal && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const r = (reasonModal.reason || "").trim();
              if (!r) { toast.error("Please add a short reason."); return; }
              const target = reasonModal.newStatus;
              setReasonModal(null);
              await saveStatus(target, r);
            }}
            className="space-y-4"
            data-testid="opp-status-reason-form"
          >
            <p className="text-xs text-[#475569]">
              This will be shown to the Solar Consultant and the customer-facing tracker so everyone understands why the opportunity is {reasonModal.newStatus.replace("_", " ")}.
            </p>
            <Field label="Reason *">
              <Textarea
                rows={4}
                autoFocus
                required
                value={reasonModal.reason}
                onChange={(e) => setReasonModal((m) => ({ ...m, reason: e.target.value }))}
                data-testid="opp-status-reason-textarea"
                placeholder="e.g. Customer postponed for 6 months due to renovation"
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E4DC]">
              <button
                type="button"
                onClick={() => setReasonModal(null)}
                className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]"
              >Cancel</button>
              <PrimaryButton
                type="submit"
                disabled={statusSaving || !(reasonModal.reason || "").trim()}
                testid="opp-status-reason-save"
              >
                {statusSaving ? "Saving…" : `Confirm ${reasonModal.newStatus.replace("_", " ")}`}
              </PrimaryButton>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={waOpen} onClose={() => setWaOpen(false)} title="WhatsApp Activity" wide>
        <WhatsAppTracker leadId={opp.lead_id} oppId={opp.id} />
      </Modal>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// ScPaymentPanel — inline editor for SC referral payment (admin)
// ─────────────────────────────────────────────────────────────────────────────
function ScPaymentPanel({ oppId, opp, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status: opp.sc_payment_status || "pending",
    amount: opp.sc_payment_amount || 0,
    notes: opp.sc_payment_notes || "",
  });
  const [scUser, setScUser] = useState(null);

  useEffect(() => {
    if (opp.sc_user_id) {
      api.get("/admin/sc").then((r) => {
        setScUser(r.data.find((u) => u.id === opp.sc_user_id));
      }).catch(() => {});
    }
  }, [opp.sc_user_id]);

  async function save() {
    try {
      const params = new URLSearchParams();
      params.set("status", form.status);
      params.set("amount", String(Number(form.amount) || 0));
      params.set("notes", form.notes || "");
      await api.put(`/opportunities/${oppId}/sc-payment?${params.toString()}`);
      toast.success("SC payment updated");
      setEditing(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  const tone = { pending: "muted", processing: "warning", paid: "success" }[form.status] || "muted";
  return (
    <div className="mt-3 pt-3 border-t border-[#EEF2EA]" data-testid="sc-payment-panel">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] flex items-center gap-1">
          🌱 SC Referral
        </div>
        {!editing && <button onClick={() => setEditing(true)} data-testid="edit-sc-payment" className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline">Edit</button>}
      </div>
      <div className="font-mono text-xs text-[#0F1A12] mt-1 truncate">{scUser?.name || "—"}{scUser?.upi_id && <span className="text-[#94A3B8] ml-1">· {scUser.upi_id}</span>}</div>
      {!editing ? (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <StatusPill value={(opp.sc_payment_status || "pending")} tone={tone} />
          <span className="font-mono text-xs font-semibold text-[#1F4E2C]">₹{Number(opp.sc_payment_amount || 0).toLocaleString("en-IN")}</span>
          {opp.sc_paid_at && <span className="font-mono text-[10px] text-[#94A3B8]">Paid {new Date(opp.sc_paid_at).toLocaleDateString("en-IN")}</span>}
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="sc-pay-status">
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="paid">Paid</option>
          </Select>
          <TextInput type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount ₹" data-testid="sc-pay-amount" />
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (UTR / payment ref…)" data-testid="sc-pay-notes" />
          <div className="flex gap-2">
            <button onClick={save} data-testid="sc-pay-save" className="flex-1 bg-[#1F4E2C] text-white text-[10px] font-mono uppercase tracking-widest py-1.5">Save</button>
            <button onClick={() => setEditing(false)} className="px-3 text-[10px] font-mono uppercase tracking-widest text-[#475569]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
